import type { ComponentProps, HTMLAttributes } from "react";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { tv } from "tailwind-variants";

import { AltArrowRightIcon, CheckIcon } from "../icons";

const styles = tv({
  slots: {
    subTrigger:
      "flex cursor-default items-center rounded-xs px-2 py-1.5 text-sm outline-hidden select-none focus:bg-primary-100 data-[state=open]:bg-neutral-50",
    subContent:
      "z-50 min-w-32 overflow-hidden rounded-md border bg-neutral-100 p-1 text-neutral-950 shadow-lg data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    content: [
      "z-50! flex min-w-(--radix-dropdown-menu-trigger-width) flex-col overflow-hidden rounded-2xl border border-primary-100 bg-neutral-50 p-4 text-neutral-950 shadow-md",
      "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    ],
    item: "relative flex cursor-pointer items-center rounded-md px-2 py-2 text-sm outline-hidden transition-colors select-none focus:bg-primary-100 focus:text-neutral-950 data-disabled:pointer-events-none data-disabled:opacity-50",
    checkboxItem:
      "relative flex cursor-default items-center rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden transition-colors select-none focus:bg-primary-100 data-disabled:pointer-events-none data-disabled:opacity-50",
    radioItem:
      "relative flex cursor-default items-center rounded-xs py-1.5 pr-2 pl-8 text-sm outline-hidden transition-colors select-none focus:bg-primary-100 data-disabled:pointer-events-none data-disabled:opacity-50",
    label: "px-2 py-1.5 text-sm font-semibold",
    separator: "-mx-1 my-1 h-px bg-primary-100",
    shortcut: "ml-auto text-xs tracking-widest opacity-60",
  },

  variants: {
    inset: {
      true: {
        subTrigger: "pl-8",
        item: "pl-8",
        label: "pl-8",
      },
    },
  },
});

const {
  subTrigger,
  subContent,
  content,
  item,
  checkboxItem,
  radioItem,
  label,
  separator,
  shortcut,
} = styles();

const Root = DropdownMenuPrimitive.Root;

const Trigger = DropdownMenuPrimitive.Trigger;

const Group = DropdownMenuPrimitive.Group;

const Portal = DropdownMenuPrimitive.Portal;

const Sub = DropdownMenuPrimitive.Sub;

const RadioGroup = DropdownMenuPrimitive.RadioGroup;

const Arrow = DropdownMenuPrimitive.Arrow;

const SubTrigger = ({
  className,
  inset,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.SubTrigger
    className={subTrigger({ inset, className })}
    {...props}
  >
    {children}
    <AltArrowRightIcon className="ml-auto h-4 w-4" />
  </DropdownMenuPrimitive.SubTrigger>
);

const SubContent = ({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.SubContent>) => (
  <DropdownMenuPrimitive.SubContent
    className={subContent({ className })}
    {...props}
  />
);

const Content = ({
  className,
  sideOffset = 4,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Content>) => (
  <DropdownMenuPrimitive.Portal>
    <DropdownMenuPrimitive.Content
      sideOffset={sideOffset}
      className={content({ className })}
      {...props}
    />
  </DropdownMenuPrimitive.Portal>
);

const Item = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.Item
    className={item({ className, inset })}
    {...props}
  />
);

const CheckboxItem = ({
  className,
  children,
  checked,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) => (
  <DropdownMenuPrimitive.CheckboxItem
    className={checkboxItem({ className })}
    checked={checked}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        <CheckIcon className="h-4 w-4" />
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.CheckboxItem>
);

const RadioItem = ({
  className,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) => (
  <DropdownMenuPrimitive.RadioItem
    className={radioItem({ className })}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <DropdownMenuPrimitive.ItemIndicator>
        IndicatorIcon
      </DropdownMenuPrimitive.ItemIndicator>
    </span>
    {children}
  </DropdownMenuPrimitive.RadioItem>
);

const Label = ({
  className,
  inset,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean;
}) => (
  <DropdownMenuPrimitive.Label
    className={label({ inset, className })}
    {...props}
  />
);

const Separator = ({
  className,
  ...props
}: ComponentProps<typeof DropdownMenuPrimitive.Separator>) => (
  <DropdownMenuPrimitive.Separator
    className={separator({ className })}
    {...props}
  />
);

const Shortcut = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => {
  return <span className={shortcut({ className })} {...props} />;
};

export const DropdownMenu = {
  Root,
  Trigger,
  Content,
  Item,
  CheckboxItem,
  RadioItem,
  Label,
  Separator,
  Shortcut,
  Group,
  Portal,
  Sub,
  SubContent,
  SubTrigger,
  RadioGroup,
  Arrow,
};
